import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase.js";
import { toast } from "react-toastify";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "accounts", label: "Bank accounts" },
  { id: "bills", label: "Vendor bills" },
  { id: "payments", label: "Payments" },
];

const BILL_STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Pending approval",
  approved: "Approved",
  paid: "Paid",
  void: "Void",
};

const CATEGORIES = [
  "landscaping",
  "maintenance",
  "utilities",
  "insurance",
  "legal",
  "roadwork",
  "other",
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function StatCard({ label, value, hint }) {
  return (
    <div className="finance-stat-card">
      <p className="finance-stat-label">{label}</p>
      <p className="finance-stat-value">{value}</p>
      {hint ? <p className="finance-stat-hint">{hint}</p> : null}
    </div>
  );
}

function AdminFinancePage({ isAdmin, isMobile }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [billFilter, setBillFilter] = useState("all");
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showBillForm, setShowBillForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingBill, setEditingBill] = useState(null);
  const [payingBill, setPayingBill] = useState(null);
  const [accountForm, setAccountForm] = useState({
    nickname: "",
    institution: "",
    accountType: "checking",
    lastFour: "",
    balance: "",
    isPrimary: true,
    notes: "",
  });
  const [billForm, setBillForm] = useState({
    contractorId: "",
    invoiceNumber: "",
    description: "",
    category: "maintenance",
    amount: "",
    dueDate: "",
    notes: "",
    status: "pending_approval",
  });
  const [paymentForm, setPaymentForm] = useState({
    bankAccountId: "",
    paymentMethod: "check",
    checkNumber: "",
    paidAt: new Date().toISOString().slice(0, 10),
    memo: "",
  });

  const loadContractors = useCallback(async () => {
    const snap = await getDocs(collection(db, "contractors"));
    const rows = snap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => String(a.businessName || "").localeCompare(String(b.businessName || "")));
    setContractors(rows);
    return rows;
  }, []);

  const loadDashboard = useCallback(async () => {
    const fn = httpsCallable(functions, "getFinanceDashboard");
    const { data } = await fn();
    setDashboard(data);
    return data;
  }, []);

  const loadAccounts = useCallback(async () => {
    const fn = httpsCallable(functions, "listBankAccounts");
    const { data } = await fn();
    setAccounts(data.accounts || []);
    return data.accounts || [];
  }, []);

  const loadBills = useCallback(async () => {
    const fn = httpsCallable(functions, "listVendorBills");
    const payload = billFilter === "all" ? {} : { status: billFilter };
    const { data } = await fn(payload);
    setBills(data.bills || []);
    return data.bills || [];
  }, [billFilter]);

  const loadPayments = useCallback(async () => {
    const fn = httpsCallable(functions, "listVendorPayments");
    const { data } = await fn();
    setPayments(data.payments || []);
    return data.payments || [];
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDashboard(),
        loadAccounts(),
        loadBills(),
        loadPayments(),
        loadContractors(),
      ]);
    } catch (error) {
      console.error("Finance refresh failed:", error);
      toast.error(`Failed to load finance data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [loadAccounts, loadBills, loadContractors, loadDashboard, loadPayments]);

  useEffect(() => {
    document.title = "HOA Finance | Nantahala Mountain Resort";
    return () => {
      document.title = "NMR Community";
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    refreshAll();
  }, [isAdmin, refreshAll]);

  useEffect(() => {
    if (!isAdmin) return;
    loadBills().catch((error) => {
      toast.error(`Failed to load bills: ${error.message}`);
    });
  }, [billFilter, isAdmin, loadBills]);

  const primaryAccountId = useMemo(
    () => accounts.find((a) => a.isPrimary)?.id || accounts[0]?.id || "",
    [accounts],
  );

  const resetAccountForm = () => {
    setAccountForm({
      nickname: "",
      institution: "",
      accountType: "checking",
      lastFour: "",
      balance: "",
      isPrimary: accounts.length === 0,
      notes: "",
    });
    setEditingAccount(null);
    setShowAccountForm(false);
  };

  const resetBillForm = () => {
    setBillForm({
      contractorId: contractors[0]?.id || "",
      invoiceNumber: "",
      description: "",
      category: "maintenance",
      amount: "",
      dueDate: "",
      notes: "",
      status: "pending_approval",
    });
    setEditingBill(null);
    setShowBillForm(false);
  };

  const handleSaveAccount = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "upsertBankAccount");
      await fn({
        accountId: editingAccount?.id,
        nickname: accountForm.nickname,
        institution: accountForm.institution,
        accountType: accountForm.accountType,
        lastFour: accountForm.lastFour,
        balance: accountForm.balance === "" ? 0 : Number(accountForm.balance),
        isPrimary: accountForm.isPrimary,
        notes: accountForm.notes,
      });
      toast.success(editingAccount ? "Bank account updated" : "Bank account added");
      resetAccountForm();
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save bank account");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBill = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "upsertVendorBill");
      await fn({
        billId: editingBill?.id,
        contractorId: billForm.contractorId,
        invoiceNumber: billForm.invoiceNumber,
        description: billForm.description,
        category: billForm.category,
        amount: Number(billForm.amount),
        dueDate: billForm.dueDate,
        notes: billForm.notes,
        status: billForm.status,
      });
      toast.success(editingBill ? "Bill updated" : "Bill created");
      resetBillForm();
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  const handleBillStatus = async (billId, status) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "updateVendorBillStatus");
      await fn({ billId, status });
      toast.success(`Bill marked ${BILL_STATUS_LABELS[status] || status}`);
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to update bill status");
    } finally {
      setSaving(false);
    }
  };

  const handleRecordPayment = async (event) => {
    event.preventDefault();
    if (!payingBill) return;
    setSaving(true);
    try {
      const fn = httpsCallable(functions, "recordVendorPayment");
      await fn({
        billId: payingBill.id,
        bankAccountId: paymentForm.bankAccountId || primaryAccountId,
        paymentMethod: paymentForm.paymentMethod,
        checkNumber: paymentForm.checkNumber,
        paidAt: paymentForm.paidAt,
        memo: paymentForm.memo,
      });
      toast.success(`Payment recorded for ${payingBill.contractorName}`);
      setPayingBill(null);
      setPaymentForm({
        bankAccountId: "",
        paymentMethod: "check",
        checkNumber: "",
        paidAt: new Date().toISOString().slice(0, 10),
        memo: "",
      });
      await refreshAll();
    } catch (error) {
      toast.error(error.message || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="docs-card">
          <h1>Access denied</h1>
          <p>HOA finance is restricted to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-finance-page ${isMobile ? "admin-finance-page--mobile" : ""}`}>
      <style>{`
        .admin-finance-page { padding: ${isMobile ? "1rem" : "2rem"}; max-width: 1200px; margin: 0 auto; }
        .admin-finance-header { margin-bottom: 1.25rem; }
        .admin-finance-title { font-size: ${isMobile ? "1.35rem" : "1.75rem"}; margin: 0 0 0.35rem; color: #1a3c34; }
        .admin-finance-subtitle { margin: 0; color: #4b5563; line-height: 1.5; }
        .finance-tabs { display: flex; flex-wrap: wrap; gap: 0.5rem; margin: 1rem 0; }
        .finance-tab { border: 1px solid #cbd5e1; background: #fff; color: #1a3c34; padding: 0.45rem 0.85rem; border-radius: 999px; cursor: pointer; }
        .finance-tab--active { background: #1a3c34; color: #fff; border-color: #1a3c34; }
        .finance-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
        .finance-stat-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.85rem; }
        .finance-stat-label { margin: 0; font-size: 0.8rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
        .finance-stat-value { margin: 0.35rem 0 0; font-size: 1.35rem; font-weight: 700; color: #0f172a; }
        .finance-stat-hint { margin: 0.25rem 0 0; font-size: 0.8rem; color: #64748b; }
        .finance-toolbar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-bottom: 0.75rem; }
        .finance-btn { background: #1a3c34; color: #fff; border: none; border-radius: 8px; padding: 0.5rem 0.9rem; cursor: pointer; }
        .finance-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .finance-btn--secondary { background: #fff; color: #1a3c34; border: 1px solid #94a3b8; }
        .finance-btn--small { padding: 0.3rem 0.55rem; font-size: 0.8rem; }
        .finance-table-wrap { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 10px; }
        .finance-table { width: 100%; border-collapse: collapse; min-width: 640px; }
        .finance-table th, .finance-table td { padding: 0.55rem 0.65rem; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 0.9rem; }
        .finance-table th { background: #1a3c34; color: #fff; }
        .finance-table tr:nth-child(even) { background: #f8fafc; }
        .finance-badge { display: inline-block; padding: 0.15rem 0.45rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; }
        .finance-badge--draft { background: #e2e8f0; color: #334155; }
        .finance-badge--pending_approval { background: #fef3c7; color: #92400e; }
        .finance-badge--approved { background: #dbeafe; color: #1e40af; }
        .finance-badge--paid { background: #dcfce7; color: #166534; }
        .finance-badge--void { background: #fee2e2; color: #991b1b; }
        .finance-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem; }
        .finance-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; }
        .finance-form-group label { display: block; font-size: 0.85rem; margin-bottom: 0.25rem; color: #334155; }
        .finance-form-group input, .finance-form-group select, .finance-form-group textarea {
          width: 100%; padding: 0.45rem 0.55rem; border: 1px solid #cbd5e1; border-radius: 8px;
        }
        .finance-form-group--full { grid-column: 1 / -1; }
        .finance-modal-overlay {
          position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1200;
          display: flex; align-items: center; justify-content: center; padding: 1rem;
        }
        .finance-modal { background: #fff; border-radius: 12px; padding: 1rem; width: min(520px, 100%); max-height: 90vh; overflow-y: auto; }
        .finance-note { font-size: 0.85rem; color: #64748b; margin-top: 0.75rem; line-height: 1.45; }
        .finance-actions { display: flex; flex-wrap: wrap; gap: 0.35rem; }
      `}</style>

      <header className="admin-finance-header">
        <h1 className="admin-finance-title">HOA Finance</h1>
        <p className="admin-finance-subtitle">
          Operating cash, vendor bills, and contractor payments. Owner assessments remain on{" "}
          <a href="/admin-invoices">Manage Invoices</a> (Stripe AR).
        </p>
      </header>

      <div className="finance-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            className={`finance-tab ${activeTab === tab.id ? "finance-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <button type="button" className="finance-btn finance-btn--secondary" disabled={loading} onClick={refreshAll}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading && !dashboard ? (
        <p>Loading finance data…</p>
      ) : (
        <>
          {activeTab === "overview" && dashboard ? (
            <section className="finance-panel">
              <div className="finance-stats">
                <StatCard label="Cash on hand" value={dashboard.cashDisplay} hint={`${dashboard.bankAccountCount} account(s)`} />
                <StatCard
                  label="AP outstanding"
                  value={dashboard.apOutstandingDisplay}
                  hint={`${dashboard.openBillCount} open bill(s)`}
                />
                <StatCard
                  label="Pending approval"
                  value={dashboard.pendingApprovalCount}
                  hint="Bills awaiting board sign-off"
                />
                <StatCard label="Paid YTD (vendors)" value={dashboard.paidYtdDisplay} />
              </div>
              <h3>Recent vendor payments</h3>
              {dashboard.recentPayments?.length ? (
                <div className="finance-table-wrap">
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recentPayments.map((row) => (
                        <tr key={row.id}>
                          <td>{row.contractorName}</td>
                          <td>{row.amountDisplay}</td>
                          <td>{row.paymentMethod}</td>
                          <td>{formatDate(row.paidAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No vendor payments recorded yet.</p>
              )}
              <p className="finance-note">
                Bank balances are tracked manually today. Plaid linking and QuickBooks/Xero sync are available as add-ons
                for automated reconciliation.
              </p>
            </section>
          ) : null}

          {activeTab === "accounts" ? (
            <section className="finance-panel">
              <div className="finance-toolbar">
                <button
                  type="button"
                  className="finance-btn"
                  onClick={() => {
                    resetAccountForm();
                    setShowAccountForm(true);
                  }}
                >
                  Add bank account
                </button>
              </div>
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Institution</th>
                      <th>Type</th>
                      <th>Last 4</th>
                      <th>Balance</th>
                      <th>As of</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.length === 0 ? (
                      <tr>
                        <td colSpan={7}>No bank accounts yet. Add your HOA operating account to track cash.</td>
                      </tr>
                    ) : (
                      accounts.map((row) => (
                        <tr key={row.id}>
                          <td>
                            {row.nickname}
                            {row.isPrimary ? " ★" : ""}
                          </td>
                          <td>{row.institution}</td>
                          <td>{row.accountType}</td>
                          <td>{row.lastFour ? `••••${row.lastFour}` : "—"}</td>
                          <td>{row.balanceDisplay}</td>
                          <td>{formatDate(row.balanceAsOf)}</td>
                          <td>
                            <button
                              type="button"
                              className="finance-btn finance-btn--secondary finance-btn--small"
                              onClick={() => {
                                setEditingAccount(row);
                                setAccountForm({
                                  nickname: row.nickname,
                                  institution: row.institution,
                                  accountType: row.accountType,
                                  lastFour: row.lastFour,
                                  balance: String((row.balanceCents || 0) / 100),
                                  isPrimary: row.isPrimary,
                                  notes: row.notes || "",
                                });
                                setShowAccountForm(true);
                              }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {activeTab === "bills" ? (
            <section className="finance-panel">
              <div className="finance-toolbar">
                <button
                  type="button"
                  className="finance-btn"
                  onClick={() => {
                    setBillForm((prev) => ({
                      ...prev,
                      contractorId: contractors[0]?.id || "",
                    }));
                    setEditingBill(null);
                    setShowBillForm(true);
                  }}
                >
                  New vendor bill
                </button>
                <select value={billFilter} onChange={(e) => setBillFilter(e.target.value)}>
                  <option value="all">All statuses</option>
                  {Object.entries(BILL_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Description</th>
                      <th>Invoice #</th>
                      <th>Due</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.length === 0 ? (
                      <tr>
                        <td colSpan={7}>No vendor bills match this filter.</td>
                      </tr>
                    ) : (
                      bills.map((row) => (
                        <tr key={row.id}>
                          <td>{row.contractorName}</td>
                          <td>{row.description}</td>
                          <td>{row.invoiceNumber || "—"}</td>
                          <td>{row.dueDate || "—"}</td>
                          <td>{row.amountDisplay}</td>
                          <td>
                            <span className={`finance-badge finance-badge--${row.status}`}>
                              {BILL_STATUS_LABELS[row.status] || row.status}
                            </span>
                          </td>
                          <td>
                            <div className="finance-actions">
                              {row.status === "draft" ? (
                                <button
                                  type="button"
                                  className="finance-btn finance-btn--small"
                                  disabled={saving}
                                  onClick={() => handleBillStatus(row.id, "pending_approval")}
                                >
                                  Submit
                                </button>
                              ) : null}
                              {row.status === "pending_approval" ? (
                                <button
                                  type="button"
                                  className="finance-btn finance-btn--small"
                                  disabled={saving}
                                  onClick={() => handleBillStatus(row.id, "approved")}
                                >
                                  Approve
                                </button>
                              ) : null}
                              {row.status === "approved" ? (
                                <button
                                  type="button"
                                  className="finance-btn finance-btn--small"
                                  disabled={saving}
                                  onClick={() => {
                                    setPayingBill(row);
                                    setPaymentForm((prev) => ({
                                      ...prev,
                                      bankAccountId: primaryAccountId,
                                    }));
                                  }}
                                >
                                  Pay
                                </button>
                              ) : null}
                              {["draft", "pending_approval", "approved"].includes(row.status) ? (
                                <button
                                  type="button"
                                  className="finance-btn finance-btn--secondary finance-btn--small"
                                  disabled={saving}
                                  onClick={() => handleBillStatus(row.id, "void")}
                                >
                                  Void
                                </button>
                              ) : null}
                              {["draft", "pending_approval"].includes(row.status) ? (
                                <button
                                  type="button"
                                  className="finance-btn finance-btn--secondary finance-btn--small"
                                  onClick={() => {
                                    setEditingBill(row);
                                    setBillForm({
                                      contractorId: row.contractorId,
                                      invoiceNumber: row.invoiceNumber,
                                      description: row.description,
                                      category: row.category,
                                      amount: String((row.amountCents || 0) / 100),
                                      dueDate: row.dueDate,
                                      notes: row.notes,
                                      status: row.status,
                                    });
                                    setShowBillForm(true);
                                  }}
                                >
                                  Edit
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {activeTab === "payments" ? (
            <section className="finance-panel">
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>From account</th>
                      <th>Memo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr>
                        <td colSpan={6}>No payments recorded yet.</td>
                      </tr>
                    ) : (
                      payments.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.paidAt)}</td>
                          <td>{row.contractorName}</td>
                          <td>{row.amountDisplay}</td>
                          <td>
                            {row.paymentMethod}
                            {row.checkNumber ? ` #${row.checkNumber}` : ""}
                          </td>
                          <td>{row.bankAccountNickname || "—"}</td>
                          <td>{row.memo || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      )}

      {showAccountForm ? (
        <div className="finance-modal-overlay" onClick={resetAccountForm}>
          <div className="finance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingAccount ? "Edit bank account" : "Add bank account"}</h2>
            <form onSubmit={handleSaveAccount}>
              <div className="finance-form-grid">
                <div className="finance-form-group">
                  <label>Nickname</label>
                  <input
                    value={accountForm.nickname}
                    onChange={(e) => setAccountForm({ ...accountForm, nickname: e.target.value })}
                    required
                  />
                </div>
                <div className="finance-form-group">
                  <label>Institution</label>
                  <input
                    value={accountForm.institution}
                    onChange={(e) => setAccountForm({ ...accountForm, institution: e.target.value })}
                    required
                  />
                </div>
                <div className="finance-form-group">
                  <label>Account type</label>
                  <select
                    value={accountForm.accountType}
                    onChange={(e) => setAccountForm({ ...accountForm, accountType: e.target.value })}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="reserve">Reserve</option>
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Last 4 digits</label>
                  <input
                    value={accountForm.lastFour}
                    onChange={(e) => setAccountForm({ ...accountForm, lastFour: e.target.value })}
                    maxLength={4}
                  />
                </div>
                <div className="finance-form-group">
                  <label>Current balance ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={accountForm.balance}
                    onChange={(e) => setAccountForm({ ...accountForm, balance: e.target.value })}
                  />
                </div>
                <div className="finance-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={accountForm.isPrimary}
                      onChange={(e) => setAccountForm({ ...accountForm, isPrimary: e.target.checked })}
                    />{" "}
                    Primary operating account
                  </label>
                </div>
                <div className="finance-form-group finance-form-group--full">
                  <label>Notes</label>
                  <textarea
                    rows={2}
                    value={accountForm.notes}
                    onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="finance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="finance-btn" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" className="finance-btn finance-btn--secondary" onClick={resetAccountForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showBillForm ? (
        <div className="finance-modal-overlay" onClick={resetBillForm}>
          <div className="finance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingBill ? "Edit vendor bill" : "New vendor bill"}</h2>
            <form onSubmit={handleSaveBill}>
              <div className="finance-form-grid">
                <div className="finance-form-group finance-form-group--full">
                  <label>Contractor</label>
                  <select
                    value={billForm.contractorId}
                    onChange={(e) => setBillForm({ ...billForm, contractorId: e.target.value })}
                    required
                  >
                    <option value="">Select contractor…</option>
                    {contractors.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.businessName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="finance-form-group finance-form-group--full">
                  <label>Description</label>
                  <input
                    value={billForm.description}
                    onChange={(e) => setBillForm({ ...billForm, description: e.target.value })}
                    required
                  />
                </div>
                <div className="finance-form-group">
                  <label>Invoice #</label>
                  <input
                    value={billForm.invoiceNumber}
                    onChange={(e) => setBillForm({ ...billForm, invoiceNumber: e.target.value })}
                  />
                </div>
                <div className="finance-form-group">
                  <label>Category</label>
                  <select
                    value={billForm.category}
                    onChange={(e) => setBillForm({ ...billForm, category: e.target.value })}
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={billForm.amount}
                    onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="finance-form-group">
                  <label>Due date</label>
                  <input
                    type="date"
                    value={billForm.dueDate}
                    onChange={(e) => setBillForm({ ...billForm, dueDate: e.target.value })}
                  />
                </div>
                {!editingBill ? (
                  <div className="finance-form-group">
                    <label>Status</label>
                    <select
                      value={billForm.status}
                      onChange={(e) => setBillForm({ ...billForm, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="pending_approval">Pending approval</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                ) : null}
                <div className="finance-form-group finance-form-group--full">
                  <label>Notes</label>
                  <textarea
                    rows={2}
                    value={billForm.notes}
                    onChange={(e) => setBillForm({ ...billForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="finance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="finance-btn" disabled={saving}>
                  {saving ? "Saving…" : "Save bill"}
                </button>
                <button type="button" className="finance-btn finance-btn--secondary" onClick={resetBillForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {payingBill ? (
        <div className="finance-modal-overlay" onClick={() => setPayingBill(null)}>
          <div className="finance-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Record payment</h2>
            <p>
              Pay <strong>{payingBill.contractorName}</strong> — {payingBill.amountDisplay} for{" "}
              {payingBill.description}
            </p>
            <form onSubmit={handleRecordPayment}>
              <div className="finance-form-grid">
                <div className="finance-form-group finance-form-group--full">
                  <label>Pay from account</label>
                  <select
                    value={paymentForm.bankAccountId || primaryAccountId}
                    onChange={(e) => setPaymentForm({ ...paymentForm, bankAccountId: e.target.value })}
                  >
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nickname} ({a.balanceDisplay})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Payment method</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  >
                    <option value="check">Check</option>
                    <option value="ach">ACH</option>
                    <option value="wire">Wire</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Check # (if applicable)</label>
                  <input
                    value={paymentForm.checkNumber}
                    onChange={(e) => setPaymentForm({ ...paymentForm, checkNumber: e.target.value })}
                  />
                </div>
                <div className="finance-form-group">
                  <label>Payment date</label>
                  <input
                    type="date"
                    value={paymentForm.paidAt}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paidAt: e.target.value })}
                    required
                  />
                </div>
                <div className="finance-form-group finance-form-group--full">
                  <label>Memo</label>
                  <input
                    value={paymentForm.memo}
                    onChange={(e) => setPaymentForm({ ...paymentForm, memo: e.target.value })}
                  />
                </div>
              </div>
              <div className="finance-toolbar" style={{ marginTop: "0.75rem" }}>
                <button type="submit" className="finance-btn" disabled={saving}>
                  {saving ? "Recording…" : "Record payment"}
                </button>
                <button type="button" className="finance-btn finance-btn--secondary" onClick={() => setPayingBill(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminFinancePage;